
import React, { useState, useMemo } from 'react';
import MarketTableRow from './market-table-row';
import MarketCoinCard from './market-coin-card';
import { useMediaQuery } from '../hooks/use-media-query';
import './market-table.css';

// Sort options exposed as chips on mobile — mirrors the sortable desktop headers.
const MOBILE_SORT_OPTIONS = [
  { key: 'market_cap_rank', label: 'Rank' },
  { key: 'current_price', label: 'Price' },
  { key: 'price_change_percentage_24h', label: '24h %' },
  { key: 'market_cap', label: 'Mkt Cap' },
  { key: 'volumeRatio', label: 'Volume' },
];

function MarketTable({
  data = [],
  searchTerm = '',
  isLoading = false,
  onRowClick,
  className = ''
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    const lowercaseSearch = searchTerm.toLowerCase();
    return data.filter(coin =>
      coin.name ? coin.name.toLowerCase().includes(lowercaseSearch) : false ||
      coin.symbol ? coin.symbol.toLowerCase().includes(lowercaseSearch) : false
    );
  }, [data, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon">&#8645;</span>;
    }
    return sortConfig.direction === 'asc'
      ? <span className="sort-icon active">&#8593;</span>
      : <span className="sort-icon active">&#8595;</span>;
  };

  if (isLoading) {
    return (
      <div className={'table-container ' + className}>
        <div className="table-loading">
          <div className="loading-spinner"></div>
          <p>Loading market data...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={'table-container ' + className}>
        <div className="table-empty">
          <p>No market data available</p>
        </div>
      </div>
    );
  }

  if (filteredData.length === 0 && searchTerm) {
    return (
      <div className={'table-container ' + className}>
        <div className="table-empty">
          <p>No cryptocurrencies found for "{searchTerm}"</p>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className={'card-list-container ' + className}>
        <div className="card-sort-chips">
          {MOBILE_SORT_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={'card-sort-chip' + (sortConfig.key === key ? ' active' : '')}
              onClick={() => handleSort(key)}
            >
              {label} {getSortIcon(key)}
            </button>
          ))}
        </div>
        <div className="card-list">
          {sortedData.map((coin, index) => (
            <MarketCoinCard
              key={coin.id || index}
              coin={coin}
              onClick={onRowClick}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={'table-container ' + className}>
      <table className="crypto-table">
        <thead>
          <tr>
            <th className="watchlist-header">
              <span>&#9733;</span>
            </th>
            <th className="sortable-header" onClick={() => handleSort('market_cap_rank')}>
              Rank {getSortIcon('market_cap_rank')}
            </th>
            <th className="sortable-header" data-tour="col-name" onClick={() => handleSort('name')}>
              Name {getSortIcon('name')}
            </th>
            <th className="sortable-header" onClick={() => handleSort('current_price')}>
              Price {getSortIcon('current_price')}
            </th>
            <th className="sortable-header" onClick={() => handleSort('price_change_percentage_24h')}>
              24h % {getSortIcon('price_change_percentage_24h')}
            </th>
            <th className="sortable-header" onClick={() => handleSort('market_cap')}>
              Market Cap {getSortIcon('market_cap')}
            </th>
            <th
              className="sortable-header"
              data-tour="col-volume"
              onClick={() => handleSort('volumeRatio')}
              title="24h volume (CoinGecko global). Colour = vs Binance historical average. ⚡ = 1.5× spike."
            >
              Volume {getSortIcon('volumeRatio')}
            </th>
            <th className="trend-header">Weekly</th>
            <th className="trend-header">Daily</th>
            <th className="trend-header">4H</th>
            <th className="trend-header sma-slope-header" title="50 SMA slope direction per timeframe">50 SMA</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((coin, index) => (
            <MarketTableRow
              key={coin.id || index}
              coin={coin}
              onClick={onRowClick}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default MarketTable;
